"use client"

import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { HugeiconsIcon, Icons } from "@/components/icons"
import { useIsMobile } from "@/hooks/use-mobile"
import { useMediaQuery } from "usehooks-ts"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Separator } from "@/components/ui/separator"

const SIDEBAR_WIDTH = "16rem"
const SIDEBAR_WIDTH_MOBILE = "18rem"
const SIDEBAR_WIDTH_ICON = "5.5rem"
const SIDEBAR_KEYBOARD_SHORTCUT = "b"

interface SidebarContextValue {
  state: "expanded" | "collapsed"
  open: boolean
  setOpen: (open: boolean) => void
  sidebarVisible: boolean
  setSidebarVisible: (visible: boolean) => void
  openMobile: boolean
  setOpenMobile: (open: boolean) => void
  isMobile: boolean
  toggleSidebar: () => void
  toggleSidebarVisibility: () => void
  /** When false, sidebar is forced collapsed and cannot expand (e.g. viewport < 1200px). */
  canExpand: boolean
}

const SidebarContext = React.createContext<SidebarContextValue | null>(null)

function useSidebar() {
  const context = React.useContext(SidebarContext)
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.")
  }
  return context
}

function SidebarProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  defaultOpen?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
}) {
  const isMobile = useIsMobile()
  const canExpand = useMediaQuery("(min-width: 1200px)")
  const [openMobile, setOpenMobile] = React.useState(false)
  const [_open, _setOpen] = React.useState(defaultOpen)
  const [sidebarVisible, setSidebarVisible] = React.useState(true)
  const open = openProp ?? _open
  const setOpen = React.useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      if (!canExpand && (typeof value === "function" ? value(open) : value)) {
        return
      }
      const openState = typeof value === "function" ? value(open) : value
      setOpenProp?.(openState)
      _setOpen(openState)
    },
    [setOpenProp, open, canExpand]
  )

  const toggleSidebar = React.useCallback(() => {
    if (isMobile) setOpenMobile((o) => !o)
    else if (canExpand) setOpen((o) => !o)
  }, [isMobile, canExpand, setOpen, setOpenMobile])

  const toggleSidebarVisibility = React.useCallback(() => {
    if (!isMobile) {
      setSidebarVisible((v) => !v)
    }
  }, [isMobile])

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault()
        toggleSidebar()
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [toggleSidebar])

  const state: "expanded" | "collapsed" =
    canExpand && open ? "expanded" : "collapsed"
  const contextValue = React.useMemo(
    () => ({
      state,
      open,
      setOpen,
      sidebarVisible,
      setSidebarVisible,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
      toggleSidebarVisibility,
      canExpand,
    }),
    [
      state,
      open,
      setOpen,
      sidebarVisible,
      setSidebarVisible,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
      toggleSidebarVisibility,
      canExpand,
    ]
  )

  return (
    <SidebarContext.Provider value={contextValue}>
      <div
        className={cn("group/sidebar flex h-svh w-full", className)}
        data-sidebar="sidebar-provider"
        style={
          {
            "--sidebar-width": SIDEBAR_WIDTH,
            "--sidebar-width-mobile": SIDEBAR_WIDTH_MOBILE,
            "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
            ...style,
          } as React.CSSProperties
        }
        {...props}
      >
        {children}
      </div>
    </SidebarContext.Provider>
  )
}

function Sidebar({
  side = "left",
  variant = "sidebar",
  collapsible = "offcanvas",
  className,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  side?: "left" | "right"
  variant?: "sidebar" | "floating" | "inset"
  collapsible?: "offcanvas" | "icon" | "none"
}) {
  const isLg = useMediaQuery("(min-width: 1024px)")
  const isMd = useMediaQuery("(min-width: 768px)")
  const { state, openMobile, setOpenMobile, canExpand, sidebarVisible } =
    useSidebar()
  const isMobile = isLg && !isMd

  if (!isLg) return null

  if (collapsible === "none") {
    return (
      <div
        className={cn("bg-sidebar flex h-svh flex-col", className)}
        data-sidebar="sidebar"
        data-state={state}
        {...props}
      >
        {children}
      </div>
    )
  }

  if (isMobile) {
    return (
      <Sheet open={openMobile} onOpenChange={setOpenMobile}>
        <SheetContent
          side="left"
          className="border-sidebar-border bg-sidebar w-[var(--sidebar-width-mobile)] max-w-[var(--sidebar-width-mobile)] p-0"
          data-sidebar="sidebar"
          data-mobile="true"
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Sidebar</SheetTitle>
            <SheetDescription>Displays the mobile sidebar.</SheetDescription>
          </SheetHeader>
          {children}
        </SheetContent>
      </Sheet>
    )
  }

  const isIcon = collapsible === "icon"
  const widthClass = !sidebarVisible
    ? "w-0"
    : isIcon && state === "collapsed"
      ? "w-[var(--sidebar-width-icon)]"
      : "w-[var(--sidebar-width)]"

  return (
    <div
      className={cn(
        "border-sidebar-border bg-sidebar relative z-10 hidden h-svh shrink-0 flex-col overflow-hidden lg:flex",
        "transition-[width,border-color] duration-300 ease-[0.25,0.1,0.25,1]",
        widthClass,
        !sidebarVisible && "border-transparent",
        side === "left" ? "border-r" : "border-l",
        className
      )}
      data-sidebar="sidebar"
      data-state={state}
      data-visible={sidebarVisible ? "true" : "false"}
      data-collapsible={collapsible}
      data-side={side}
      {...props}
    >
      <div
        className={cn(
          "flex h-full flex-col transition-opacity duration-200 ease-[0.25,0.1,0.25,1]",
          !sidebarVisible && "pointer-events-none opacity-0"
        )}
      >
        {children}
        {isIcon && canExpand && sidebarVisible && <SidebarRail />}
      </div>
    </div>
  )
}

function SidebarRail({ className, ...props }: React.ComponentProps<"button">) {
  const { toggleSidebar } = useSidebar()
  return (
    <button
      type="button"
      aria-label="Toggle sidebar"
      className={cn(
        "hover:bg-sidebar-border absolute inset-y-0 top-0 right-0 z-20 hidden h-full w-2 max-w-2 cursor-col-resize transition-colors sm:flex",
        "after:absolute after:inset-y-0 after:left-1/2 after:w-px after:-translate-x-1/2 after:bg-transparent",
        className
      )}
      onClick={toggleSidebar}
      {...props}
    />
  )
}

function SidebarTrigger({
  className,
  onClick,
  ...props
}: React.ComponentProps<typeof Button>) {
  const { toggleSidebar } = useSidebar()
  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn(className)}
      onClick={(event) => {
        onClick?.(event)
        toggleSidebar()
      }}
      {...props}
    >
      <HugeiconsIcon icon={Icons.Menu01} size={20} className="rtl:rotate-180" />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  )
}

function SidebarInset({ className, ...props }: React.ComponentProps<"main">) {
  return (
    <main
      className={cn(
        "bg-background relative flex min-h-svh min-w-0 flex-1 flex-col overflow-hidden",
        className
      )}
      {...props}
    />
  )
}

function SidebarHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "flex flex-col gap-2 p-2 data-[sidebar=sidebar]:p-2",
        className
      )}
      data-sidebar="sidebar-header"
      {...props}
    />
  )
}

function SidebarFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "flex flex-col gap-2 p-2 data-[sidebar=sidebar]:p-2",
        className
      )}
      data-sidebar="sidebar-footer"
      {...props}
    />
  )
}

function SidebarSeparator({
  className,
  ...props
}: React.ComponentProps<typeof Separator>) {
  return (
    <Separator
      className={cn("bg-sidebar-border mx-2 w-auto", className)}
      data-sidebar="sidebar-separator"
      {...props}
    />
  )
}

function SidebarContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "flex min-h-0 flex-1 flex-col gap-2 overflow-auto group-data-[collapsible=icon]:overflow-hidden",
        className
      )}
      data-sidebar="sidebar-content"
      {...props}
    />
  )
}

function SidebarGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn("relative flex w-full min-w-0 flex-col p-2", className)}
      data-sidebar="sidebar-group"
      {...props}
    />
  )
}

function SidebarGroupLabel({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<"div"> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "div"
  return (
    <Comp
      className={cn(
        "text-sidebar-foreground/70 ring-sidebar-ring rounded-md px-2 py-1.5 text-xs font-medium outline-none focus-visible:ring-2 [&>svg]:size-3.5",
        "transition-[width,height,padding] duration-300 ease-[0.25,0.1,0.25,1]",
        className
      )}
      data-sidebar="sidebar-group-label"
      {...props}
    />
  )
}

function SidebarGroupContent({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      className={cn("w-full text-sm", className)}
      data-sidebar="sidebar-group-content"
      {...props}
    />
  )
}

function SidebarMenu({ className, ...props }: React.ComponentProps<"ul">) {
  return (
    <ul
      className={cn("flex w-full min-w-0 flex-col gap-1", className)}
      data-sidebar="sidebar-menu"
      {...props}
    />
  )
}

function SidebarMenuItem({ className, ...props }: React.ComponentProps<"li">) {
  return (
    <li
      className={cn("group/menu-item relative", className)}
      data-sidebar="sidebar-menu-item"
      {...props}
    />
  )
}

const sidebarMenuButtonVariants = cva(
  "peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-none ring-sidebar-ring transition-[width,height,padding,gap] duration-300 ease-[0.25,0.1,0.25,1] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "",
        outline:
          "bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]",
      },
      size: {
        default: "h-8 text-sm",
        sm: "h-7 text-xs",
        lg: "h-12 text-sm",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function SidebarMenuButton({
  asChild = false,
  isActive = false,
  variant = "default",
  size = "default",
  className,
  ...props
}: React.ComponentProps<"button"> & {
  asChild?: boolean
  isActive?: boolean
} & VariantProps<typeof sidebarMenuButtonVariants>) {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(
        sidebarMenuButtonVariants({ variant, size }),
        "data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground",
        className
      )}
      data-active={isActive}
      data-sidebar="sidebar-menu-button"
      {...props}
    />
  )
}

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
}
